import React, { useContext, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import API from '../../../services/API'
import {
  Alert,
  Button,
  LinearProgress,
  Link,
  Paper,
  Stack,
  Typography,
} from '@mui/material'
import { DetailRow } from '../../../components/Cards/DetailsBox'
import Box from '@mui/material/Box'
import PriorityHighIcon from '@mui/icons-material/PriorityHigh'
import CheckBoxIcon from '@mui/icons-material/CheckBox'
import { GlobalContext, UserContext } from '../../../App'
import moment from 'moment'
import AddNewSOP from './AddNewSOP'
import BigModal from '../../../components/Modals/BigModal'
import SmallModal from '../../../components/Modals/SmallModal'

const SingleSOP = () => {
  const [loading, setLoading] = useState(true)
  const [sop, setSOP] = useState({})
  const [docURL, setDocURL] = useState()
  const [notFound, setNotFound] = useState(false)
  const { id } = useParams()
  const { user } = useContext(UserContext)
  const isGovernanceUser = user.employee?.teams.some(
    (team) => team === 'Governance'
  )
  const [acknowledgedByCurrentUser, setAcknowledgedByCurrentUser] =
    useState(false)
  const navigate = useNavigate()
  const { setToast } = useContext(GlobalContext)
  const [openConfirmDelete, setOpenConfirmDelete] = useState(false)
  const [openEdit, setOpenEdit] = useState(false)

  const loadSOP = () => {
    setLoading(true)
    API.get('/sop/' + id).then((res) => {
      if (res.status === 200) {
        setSOP(res.data)
        setAcknowledgedByCurrentUser(res.data.isAcknowledged)
      } else {
        setNotFound(true)
      }
    })
  }

  useEffect(() => {
    loadSOP()
  }, [id])

  useEffect(() => {
    if (sop.docLink) {
      API.post('/gets3downloadurl', {
        key: sop.docLink,
        ACL: 'pulic-read',
        expires: 3000,
      })
        .then((res) => {
          if (res.status === 200) {
            setDocURL(res.data)
          }
        })
        .catch((e) => console.error(e))
        .finally(() => setLoading(false))
    }
  }, [sop.docLink])

  const handleMarkAsRead = () => {
    API.post('/sop-markasread', {
      sopID: sop._id,
    })
      .then((res) => {
        if (res.status === 200) {
          setAcknowledgedByCurrentUser(true)
        }
      })
      .catch((e) => console.error(e))
  }

  const handleDelete = () => {
    API.delete('/sop/' + sop._id).then((res) => {
      if (res.status === 200) {
        navigate('/standard-operating-procedures')
        setToast({
          message: 'SOP Deleted',
        })
      } else {
        setToast({
          message: 'Unable to delete SOP. Please try again later.',
          severity: 'error',
        })
      }
    })
  }

  const PDFButton = () => (
    <Link
      href={docURL}
      target="_blank"
      sx={{ textDecoration: 'none' }}
      width="100%"
    >
      <Button variant="contained" color="primary" fullWidth>
        View Policy
      </Button>
    </Link>
  )

  return (
    <Stack maxHeight="100vh" minHeight="100%">
      {loading && <LinearProgress />}
      {notFound ? (
        <Stack gap={1}>
          <Alert severity="error">
            Unable to load SOP. Please try again later.
          </Alert>
          <Button
            onClick={() => navigate('/standard-operating-procedures')}
            color="primary"
            fullWidth
            variant="outlined"
          >
            Go Back
          </Button>
        </Stack>
      ) : (
        <>
          <Stack
            direction={{ xs: 'column', lg: 'row' }}
            justifyContent="space-between"
          >
            <Typography variant="title2" display="flex">
              {sop?.title}
            </Typography>
            {isGovernanceUser && (
              <Stack direction="row" gap={1}>
                <Button
                  onClick={() => setOpenEdit(true)}
                  size="small"
                  color="anMainBlue"
                  variant="contained"
                >
                  Edit
                </Button>
                <BigModal open={openEdit} onClose={() => setOpenEdit(false)}>
                  <AddNewSOP
                    existingSOP={sop}
                    onCloseSubmit={(newSOPInfo) => {
                      setOpenEdit(false)
                      setSOP((prev) => ({ ...prev, ...newSOPInfo }))
                    }}
                  />
                </BigModal>
                <Button
                  onClick={() => setOpenConfirmDelete(true)}
                  size="small"
                  color="anRed"
                  variant="contained"
                >
                  Delete
                </Button>
                <SmallModal
                  open={openConfirmDelete}
                  onClose={() => setOpenConfirmDelete(false)}
                >
                  <Stack gap={0.5}>
                    <Typography textAlign="center">
                      Are you sure you want to delete this PGD?
                    </Typography>
                    <Button
                      color="error"
                      onClick={handleDelete}
                      variant="contained"
                    >
                      Yes - Delete
                    </Button>
                    <Button
                      color="primary"
                      onClick={() => setOpenConfirmDelete(false)}
                      variant="contained"
                    >
                      No - Cancel
                    </Button>
                  </Stack>
                </SmallModal>
              </Stack>
            )}
          </Stack>
          <Typography variant="subtitle1">{sop?.sopNumber}</Typography>
          <Stack direction="row" minHeight="90%" gap={2}>
            <Box height="100%" display={{ xs: 'none', lg: 'flex' }} flex={5}>
              <object
                data={docURL}
                type="application/pdf"
                width="100%"
                height="100%"
              >
                <p>
                  Browser does not support PDF files. Please use the button
                  below to view this document.
                </p>
                <PDFButton />
              </object>
            </Box>
            <Box padding={1} flex={2} boverflow="hidden">
              <Stack gap={2}>
                <Box display={{ xs: 'flex', lg: 'none' }}>
                  <PDFButton />
                </Box>
                <Box component={Paper} padding={1}>
                  <Stack gap={0.5}>
                    <DetailRow
                      label="Version"
                      value={sop?.version}
                      fullWidthLabel
                    />
                    <DetailRow label="Type" value={sop?.type} fullWidthLabel />
                    <DetailRow
                      label="Sop Number"
                      value={sop?.sopNumber}
                      fullWidthLabel
                    />
                    <DetailRow
                      label="Date"
                      value={
                        sop?.date ? moment(sop.date).format('Do MMM YYYY') : ''
                      }
                      fullWidthLabel
                    />
                    <DetailRow
                      label="Review Date"
                      value={
                        sop?.reviewDate
                          ? moment(sop.reviewDate).format('Do MMM YYYY')
                          : ''
                      }
                      fullWidthLabel
                    />
                    <DetailRow
                      label="FAO"
                      fullWidthLabel
                      value={
                        sop?.faoAllStaff
                          ? 'All Staff'
                          : sop?.faoRoles?.join(', ')
                      }
                    />
                    {sop?.requiresAcknowledgementByCurrentUser ? (
                      <Button
                        color={'error'}
                        disabled={acknowledgedByCurrentUser}
                        variant="contained"
                        fullWidth
                        onClick={() => handleMarkAsRead()}
                        startIcon={
                          acknowledgedByCurrentUser ? (
                            <CheckBoxIcon />
                          ) : (
                            <PriorityHighIcon />
                          )
                        }
                      >
                        {acknowledgedByCurrentUser
                          ? 'Acknowledged'
                          : 'Mark as Read'}
                      </Button>
                    ) : (
                      <Typography variant="subtitle2" textAlign="center">
                        This SOP does not require your acknowledgement.
                      </Typography>
                    )}
                  </Stack>
                </Box>
                {isGovernanceUser && (
                  <Box
                    component={Paper}
                    padding={1}
                    maxHeight="20rem"
                    overflow="auto"
                  >
                    <Typography variant="title3">Acknowledged By:</Typography>
                    <Stack>
                      {sop?.requiredStaff
                        ?.sort((a, b) => {
                          const ackIds =
                            sop?.acknowledgedBy?.map((x) => x._id) || []

                          const aAck = ackIds.includes(a._id)
                          const bAck = ackIds.includes(b._id)

                          if (aAck !== bAck) return aAck ? -1 : 1

                          const aName = `${a.firstName ?? ''} ${
                            a.lastName ?? ''
                          }`.toLowerCase()
                          const bName = `${b.firstName ?? ''} ${
                            b.lastName ?? ''
                          }`.toLowerCase()

                          return aName.localeCompare(bName)
                        })
                        .map((staff) => (
                          <Box
                            display="flex"
                            key={staff._id}
                            gap={1}
                            alignItems="center"
                          >
                            <Box
                              width="0.8rem"
                              height="0.8rem"
                              borderRadius="50%"
                              bgcolor={
                                sop?.acknowledgedBy
                                  ?.map((x) => x._id)
                                  .includes(staff?._id)
                                  ? 'green'
                                  : 'red'
                              }
                            />
                            <Box
                              onClick={() =>
                                navigate(`/employees/${staff._id}`)
                              }
                              sx={{ cursor: 'pointer' }}
                            >
                              {staff?.firstName} {staff?.lastName}
                            </Box>
                          </Box>
                        ))}
                    </Stack>
                  </Box>
                )}
              </Stack>
            </Box>
          </Stack>
        </>
      )}
    </Stack>
  )
}

export default SingleSOP
